import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import CropSquareIcon from '@mui/icons-material/CropSquare'
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode'
import FilterNoneIcon from '@mui/icons-material/FilterNone'
import MenuIcon from '@mui/icons-material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RemoveIcon from '@mui/icons-material/Remove'
import RefreshIcon from '@mui/icons-material/Refresh'
import StorageIcon from '@mui/icons-material/Storage'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import { Box, Chip, LinearProgress, Menu, MenuItem, Select, SelectChangeEvent, Stack, Tooltip, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FC, MouseEvent, useEffect, useMemo, useState } from 'react'
import { getServerOptions } from '../config/server-options'
import AppHeaderControls from './AppHeaderControls'

export const TITLEBAR_HEIGHT = 92

interface TitleBarProps {
  toggleTheme: () => void
  increaseFontSize: () => void
  decreaseFontSize: () => void
  canIncrease: boolean
  canDecrease: boolean
  handleDrawerOpen: () => void
  isLoaderActive: boolean
  selectedServerUrl: string | null
  onServerChange: (serverUrl: string) => void
}

const isElectron = typeof window !== 'undefined' && !!window.electronAPI

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const WindowControl: FC<{
  onClick: () => void
  children: React.ReactNode
  label: string
  danger?: boolean
}> = ({ onClick, children, label, danger }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const color = isDark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.72)'

  return (
    <Tooltip title={label} placement="bottom" arrow>
      <Box
        onClick={onClick}
        sx={{
          alignItems: 'center',
          color,
          cursor: 'pointer',
          display: 'flex',
          height: TITLEBAR_HEIGHT,
          justifyContent: 'center',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          width: 48,
          WebkitAppRegion: 'no-drag',
          '&:hover': danger
            ? {
                bgcolor: '#E81123',
                color: '#ffffff',
              }
            : {
                bgcolor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)',
              },
        } as any}
      >
        {children}
      </Box>
    </Tooltip>
  )
}

const BarBtn: FC<{
  onClick: (event: MouseEvent<HTMLElement>) => void
  disabled?: boolean
  children: React.ReactNode
  label: string
}> = ({ onClick, disabled, children, label }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  return (
    <Tooltip title={label} placement="bottom" arrow>
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 32,
          borderRadius: '10px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          bgcolor: isDark ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.5)',
          border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
          transition: 'all 0.15s ease',
          WebkitAppRegion: 'no-drag',
          '&:hover': disabled
            ? {}
            : {
                bgcolor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
                transform: 'scale(1.08)',
              },
        } as any}
      >
        {children}
      </Box>
    </Tooltip>
  )
}

const TitleBar: FC<TitleBarProps> = ({
  toggleTheme,
  increaseFontSize,
  decreaseFontSize,
  canIncrease,
  canDecrease,
  handleDrawerOpen,
  isLoaderActive,
  selectedServerUrl,
  onServerChange,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [isMaximized, setIsMaximized] = useState(true)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)
  const [showRightExtras, setShowRightExtras] = useState(() => {
    return localStorage.getItem('showHeaderRightExtras') !== 'false'
  })
  const serverOptions = useMemo(() => {
    const options = getServerOptions()
    const selectedUrl = selectedServerUrl ? normalizeUrl(selectedServerUrl) : ''
    const hasSelectedUrl = options.some((option) => option.url === selectedUrl)

    if (selectedUrl && !hasSelectedUrl) {
      return [
        ...options,
        {
          id: `selected-${selectedUrl}`,
          name: 'Current custom',
          url: selectedUrl,
        },
      ]
    }

    return options
  }, [selectedServerUrl])

  useEffect(() => {
    if (!isElectron) return
    window.electronAPI!.isMaximized().then(setIsMaximized)
    const cleanup = window.electronAPI!.onWindowStateChange(setIsMaximized)
    return cleanup
  }, [])

  if (!isElectron) return null

  const accentColor = isDark ? theme.palette.primary.main : theme.palette.secondary.main
  const accentContrast = theme.palette.getContrastText(accentColor)
  const gradient = isDark
    ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.72)} 54%, ${alpha(theme.palette.secondary.main, 0.5)} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 52%, ${alpha(theme.palette.secondary.main, 0.52)} 100%)`

  const textColor = isDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.7)'
  const selectedServerHealth = selectedServerUrl
    ? healthByUrl[normalizeUrl(selectedServerUrl)]
    : undefined
  const selectedServerName = serverOptions.find((option) => option.url === selectedServerUrl)?.name || 'Server'

  const getHealthLabel = (health?: ServerHealth) => {
    if (!health || health.status === 'checking') return 'Checking'
    if (health.status === 'healthy') return `${health.ping}ms`
    return 'Offline'
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const closeActionMenu = () => setActionMenuAnchor(null)

  const runAction = (action: () => void) => {
    action()
    closeActionMenu()
  }

  const toggleRightExtras = () => {
    setShowRightExtras((prev) => {
      const next = !prev
      localStorage.setItem('showHeaderRightExtras', String(next))
      return next
    })
    closeActionMenu()
  }

  const openDeveloperSettings = () => {
    window.electronAPI?.toggleDevTools()
  }

  return (
    <Box
      sx={{
        height: `${TITLEBAR_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        background: gradient,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        px: 2.25,
        userSelect: 'none',
        WebkitAppRegion: 'drag',
        boxShadow: isDark
          ? '0 1px 0 rgba(0,0,0,0.4), 0 18px 42px rgba(0,0,0,0.28)'
          : '0 1px 0 rgba(255,255,255,0.52), 0 18px 38px rgba(0,0,0,0.1)',
      } as any}
    >
      {isLoaderActive && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 2,
            backgroundColor: alpha(accentColor, 0.18),
            '& .MuiLinearProgress-bar': { backgroundColor: accentColor },
          }}
        />
      )}

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ width: 258, flexShrink: 0, WebkitAppRegion: 'no-drag' } as any}
      >
        <Tooltip title="Open menu" placement="bottom" arrow>
          <Box
            onClick={handleDrawerOpen}
            sx={{
              alignItems: 'center',
              bgcolor: alpha(accentColor, isDark ? 0.18 : 0.12),
              border: `1px solid ${alpha(accentColor, 0.28)}`,
              borderRadius: '14px',
              boxShadow: `0 10px 24px ${alpha(accentColor, isDark ? 0.18 : 0.1)}`,
              color: accentColor,
              cursor: 'pointer',
              display: 'flex',
              height: 44,
              justifyContent: 'center',
              transition: 'all 0.18s ease',
              width: 52,
              WebkitAppRegion: 'no-drag',
              '&:hover': {
                bgcolor: accentColor,
                color: accentContrast,
                transform: 'translateY(-1px)',
              },
            } as any}
          >
            <MenuIcon sx={{ fontSize: 26 }} />
          </Box>
        </Tooltip>
        <Box minWidth={0}>
          <Typography noWrap sx={{ color: textColor, fontSize: '1.2rem', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.005em' }}>
            Bill Desk
          </Typography>
          <Typography noWrap sx={{ color: isDark ? 'rgba(255,255,255,0.64)' : 'rgba(0,0,0,0.56)', fontSize: '0.82rem', fontWeight: 900, letterSpacing: '0.05em' }}>
            POS WORKSTATION
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, px: 1.5, WebkitAppRegion: 'no-drag' } as any}>
        <AppHeaderControls
          handleDrawerOpen={handleDrawerOpen}
          variant="titlebar"
          showMenuButton={false}
          showRightExtras={showRightExtras}
        />
      </Box>

      <Stack
        direction="row"
        spacing="8px"
        alignItems="center"
        sx={{ flexShrink: 0, WebkitAppRegion: 'no-drag' } as any}
      >
        {showRightExtras && (
          <Select
            size="small"
            value={selectedServerUrl || ''}
            onChange={(event: SelectChangeEvent) => onServerChange(event.target.value)}
            displayEmpty
            MenuProps={{
              sx: { zIndex: 13050 },
              PaperProps: {
                sx: { zIndex: 13050 },
              },
            }}
            renderValue={() => (
              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                <StorageIcon sx={{ color: textColor, fontSize: 18, opacity: 0.72 }} />
                <Typography noWrap sx={{ color: textColor, fontSize: '0.92rem', fontWeight: 900, maxWidth: 130 }}>
                  {selectedServerName}
                </Typography>
                <Chip
                  size="small"
                  label={getHealthLabel(selectedServerHealth)}
                  color={selectedServerHealth?.status === 'offline' ? 'error' : 'success'}
                  variant={selectedServerHealth?.status === 'checking' ? 'outlined' : 'filled'}
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    '& .MuiChip-label': { px: 0.85 },
                  }}
                />
              </Stack>
            )}
            sx={{
              borderRadius: '14px',
              color: textColor,
              minWidth: 258,
              maxWidth: 284,
              bgcolor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.46)',
              '& .MuiSelect-select': {
                alignItems: 'center',
                display: 'flex',
                minHeight: 'unset',
                py: 0.75,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.24)',
              },
              '& .MuiSvgIcon-root': { color: textColor },
            }}
          >
            {serverOptions.map((option) => {
              const health = healthByUrl[option.url]
              return (
                <MenuItem key={option.id} value={option.url}>
                  <Stack direction="row" spacing={1.5} alignItems="center" width="100%" minWidth={0}>
                    <Box minWidth={0} flex={1}>
                      <Typography noWrap fontWeight={800} fontSize="0.85rem">
                        {option.name}
                      </Typography>
                      <Typography noWrap variant="caption" color="text.secondary">
                        {option.url}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getHealthLabel(health)}
                      color={health?.status === 'offline' ? 'error' : 'success'}
                      variant={health?.status === 'checking' ? 'outlined' : 'filled'}
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>
                </MenuItem>
              )
            })}
          </Select>
        )}
        <BarBtn
          onClick={toggleRightExtras}
          label={showRightExtras ? 'Hide header extras' : 'Show header extras'}
        >
          {showRightExtras ? (
            <VisibilityOffRoundedIcon sx={{ fontSize: 18, color: textColor }} />
          ) : (
            <TuneRoundedIcon sx={{ fontSize: 18, color: textColor }} />
          )}
        </BarBtn>
        <BarBtn
          onClick={(event) => setActionMenuAnchor(event.currentTarget)}
          label="App actions"
        >
          <MoreVertIcon sx={{ fontSize: 18, color: textColor }} />
        </BarBtn>
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={closeActionMenu}
          MenuListProps={{ dense: true }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 13050 }}
          PaperProps={{ sx: { zIndex: 13050 } }}
        >
          <MenuItem onClick={() => runAction(openDeveloperSettings)}>
            <DeveloperModeIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Browser developer tools
          </MenuItem>
          <MenuItem onClick={toggleRightExtras}>
            {showRightExtras ? (
              <ChevronRightIcon sx={{ mr: 1.5, fontSize: 20 }} />
            ) : (
              <ChevronLeftIcon sx={{ mr: 1.5, fontSize: 20 }} />
            )}
            {showRightExtras ? 'Hide right extras' : 'Show right extras'}
          </MenuItem>
          <MenuItem onClick={() => runAction(decreaseFontSize)} disabled={!canDecrease}>
            <Typography sx={{ width: 24, fontSize: '0.75rem', fontWeight: 900 }}>A-</Typography>
            Decrease font size
          </MenuItem>
          <MenuItem onClick={() => runAction(increaseFontSize)} disabled={!canIncrease}>
            <Typography sx={{ width: 24, fontSize: '0.9rem', fontWeight: 900 }}>A+</Typography>
            Increase font size
          </MenuItem>
          <MenuItem onClick={() => runAction(toggleTheme)}>
            {isDark ? (
              <Brightness7Icon sx={{ mr: 1.5, fontSize: 18 }} />
            ) : (
              <Brightness4Icon sx={{ mr: 1.5, fontSize: 18 }} />
            )}
            {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          </MenuItem>
          <MenuItem onClick={() => runAction(handleRefresh)}>
            <RefreshIcon sx={{ mr: 1.5, fontSize: 18 }} />
            Reload app
          </MenuItem>
        </Menu>
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ mr: -2, ml: 1, WebkitAppRegion: 'no-drag' } as any}>
        <WindowControl onClick={() => window.electronAPI!.minimize()} label="Minimize">
          <RemoveIcon sx={{ fontSize: 17 }} />
        </WindowControl>
        <WindowControl
          onClick={() => window.electronAPI!.maximize()}
          label={isMaximized ? 'Restore' : 'Maximize'}
        >
          {isMaximized ? (
            <FilterNoneIcon sx={{ fontSize: 15 }} />
          ) : (
            <CropSquareIcon sx={{ fontSize: 15 }} />
          )}
        </WindowControl>
        <WindowControl onClick={() => window.electronAPI!.close()} label="Close" danger>
          <CloseIcon sx={{ fontSize: 18 }} />
        </WindowControl>
      </Stack>
    </Box>
  )
}

export default TitleBar
